import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { repo } from "./repo.js";
import { templatesFor } from "./recurring.js";
import type { RecurringTemplate } from "./db.js";

// Re-render on any repo change. Components read repo state directly after
// subscribing through this hook.
export function useRepoVersion(): number {
  return useSyncExternalStore(repo.subscribe, repo.getVersion);
}

// Recurring templates for a group, plus an invalidator to call after any
// create/apply/delete. Shared by the Expenses banner and the Settings card.
export function useRecurringTemplates(
  groupId: string,
): readonly [RecurringTemplate[], () => void] {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    void templatesFor(groupId).then(setTemplates);
  }, [groupId, reloadKey]);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return [templates, reload] as const;
}

export type Route =
  | { name: "home" }
  | { name: "join"; fragment: string }
  | { name: "group"; id: string; tab: string };

export function parseRoute(hash: string): Route {
  if (hash.startsWith("#ps=1&") || hash.startsWith("ps=1&")) {
    return { name: "join", fragment: hash };
  }
  const group = /^#\/g\/([0-9a-f]{64})(?:\/(\w+))?/.exec(hash);
  if (group) return { name: "group", id: group[1] as string, tab: group[2] ?? "expenses" };
  return { name: "home" };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export function navigate(hash: string): void {
  window.location.hash = hash;
}
